import React, { useEffect } from 'react'
import styled from 'styled-components'
import {
  Box,
  Card,
  getPaletteColor,
  Flex,
  Hide,
  Text,
  BlockLink
} from 'pcln-design-system'
import { WebPBackgroundImage } from '@pcln/content-tile'
import {
  useContentful,
  getVariantContent,
  ContentfulRichText,
  ToutType
} from '@pcln/contentful-components'
import { useFeature } from '@pcln/feature-components'
import CONTENT from './content'
import gaAnalytics from '../../../utils/analytics'
import useSeti from '../../CustomHooks/useSeti'

const Wrapper = styled(Card)`
  height: 340px;
  ${props => props.theme.mediaQueries[3]} {
    height: 250px;
  }
  background-color: white;
  border-radius: 4px;
`

const FlexWithTopBorder = styled(Flex)<{ borderColor: string }>`
  border-top: 6px solid ${props => getPaletteColor(props.borderColor)(props)};
  height: 100%;
  border-radius: 3px;
`

const ImageOnRightWebP = styled(WebPBackgroundImage)`
  align-self: flex-start;
  height: 100%;
  object-fit: cover;
  object-position: -40px;
  position: relative;
  border-bottom-right-radius: 3px;
  right: 0;
  background-color: #fff;
`

const ImageOnTopWebP = styled(WebPBackgroundImage)`
  width: 100%;
  flex-shrink: 0;
  object-fit: cover;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
  background-color: #fff;
`

const TextWrapper = styled(Flex)`
  height: 210px;
  width: 100%;
  ${props => props.theme.mediaQueries[3]} {
    height: 240px;
  }
`

const BoxLink = styled(BlockLink)`
  height: 100%;
  &:hover {
    text-decoration: none !important;
    color: inherit;
    overflow: visible;
    box-shadow: rgba(0, 0, 0, 0.08) 0px 0px 6px 0px,
      rgba(0, 0, 0, 0.16) 0px 4px 16px 0px;
    border-width: 1px;
    border-color: rgb(192, 202, 213);
    border-image: initial;
    border-radius: 3px;
  }
`

const PromoBox = ({
  children,
  boxLink,
  ariaLabel,
  googleAnalyticsTrackingOnClick
}: {
  children: React.ReactNode
  boxLink?: string | null
  ariaLabel?: string
  googleAnalyticsTrackingOnClick: Record<string, unknown>
}) => {
  return boxLink ? (
    <BoxLink
      onClick={() => gaAnalytics.fireEvent(googleAnalyticsTrackingOnClick)}
      href={boxLink}
      aria-label={ariaLabel}
    >
      {children}
    </BoxLink>
  ) : (
    <Box>{children}</Box>
  )
}

function getContentfulPromoContent(promo: ToutType) {
  const { url, ariaLabel, googleAnalyticsTracking } =
    promo.callsToAction?.[0] ?? {}
  const promoImage = promo.image ?? null
  const desktopFocalPoint = promoImage?.desktopFocalPoint ?? 'center'
  const tabletFocalPoint = promoImage?.tabletFocalPoint ?? 'center'

  return {
    headline: promo.headline,
    subheadline: promo.subheadline,
    disclaimer: promo.disclaimer,
    desktopImage: `${promoImage?.asset?.file.url}?w=330&h=448&f=${desktopFocalPoint}&fit=fill`,
    desktopImageWebP: `${promoImage?.asset?.file.url}?w=330&h=448&f=${desktopFocalPoint}&fit=fill&fm=webp`,
    tabletImage: `${promoImage?.asset?.file.url}?w=660&h=224&f=${tabletFocalPoint}&fit=fill`,
    tabletImageWebP: `${promoImage?.asset?.file.url}?w=660&h=224&f=${tabletFocalPoint}&fit=fill&fm=webp`,
    boxLink: url,
    ariaLabel,
    googleAnalyticsTrackingOnView: {
      category: promo.googleAnalyticsTracking?.category || 'promotion',
      action: promo.googleAnalyticsTracking?.action,
      label: promo.googleAnalyticsTracking?.label
    },
    googleAnalyticsTrackingOnClick: {
      category: googleAnalyticsTracking?.category,
      action: googleAnalyticsTracking?.action,
      label: googleAnalyticsTracking?.label
    },
    borderTopColor: promo.theme?.borderColor
  }
}

export default function IncredibleTile() {
  const contentfulEnabled = useFeature('siteExperience.contentfulEnabled', true)
  const incredibleTilePromo: any = useContentful('incredibleTile')
  const setiName = useSeti(
    incredibleTilePromo?.experimentName,
    incredibleTilePromo?.experimentName !== undefined
  )
  const promo = incredibleTilePromo?.experimentName
    ? getVariantContent(setiName, incredibleTilePromo)
    : incredibleTilePromo
  const contentfulEnabledAndPromo = contentfulEnabled && promo != null
  const {
    headline,
    subheadline,
    disclaimer,
    desktopImage,
    desktopImageWebP,
    tabletImage,
    tabletImageWebP,
    boxLink,
    ariaLabel,
    googleAnalyticsTrackingOnClick,
    googleAnalyticsTrackingOnView,
    borderTopColor
  } = contentfulEnabledAndPromo ? getContentfulPromoContent(promo) : CONTENT

  useEffect(() => {
    if (
      googleAnalyticsTrackingOnView?.action &&
      googleAnalyticsTrackingOnView?.label
    ) {
      gaAnalytics.fireEvent({
        category: googleAnalyticsTrackingOnView.category,
        action: googleAnalyticsTrackingOnView.action,
        label: googleAnalyticsTrackingOnView.label
      })
    }
  }, [googleAnalyticsTrackingOnView])
  return (
    <Wrapper
      boxShadowSize="md"
      mx={2}
      width={['30%', null, null, null, '410px']}
    >
      <PromoBox
        boxLink={boxLink}
        googleAnalyticsTrackingOnClick={googleAnalyticsTrackingOnClick}
        ariaLabel={ariaLabel}
      >
        <FlexWithTopBorder
          flexDirection={['column', null, null, null, 'row']}
          borderColor={borderTopColor || 'primary.base'}
        >
          <Hide xl xxl>
            <ImageOnTopWebP
              height="115px"
              image={tabletImage}
              imageWebP={tabletImageWebP}
            />
          </Hide>
          <TextWrapper flexDirection="column" p={3} pb={0}>
            <Text
              mt="-6px"
              fontSize={5}
              color="primary"
              fontWeight="bold"
              lineHeight="36px"
            >
              {headline}
            </Text>
            <Text fontSize={1} color="text" mt={2} lineHeight="24px">
              {subheadline}
            </Text>
            {contentfulEnabledAndPromo ? (
              <Box mt={[20, null, null, 'auto']}>
                <ContentfulRichText
                  color="darkGray"
                  fontSize={11}
                  body={disclaimer}
                />
              </Box>
            ) : (
              <Text
                color="darkGray"
                fontSize="11px"
                mt={[20, null, null, 'auto']}
              >
                {disclaimer}
              </Text>
            )}
          </TextWrapper>
          <Hide xs sm md lg width={1}>
            <ImageOnRightWebP
              height="100%"
              image={desktopImage}
              imageWebP={desktopImageWebP}
            />
          </Hide>
        </FlexWithTopBorder>
      </PromoBox>
    </Wrapper>
  )
}
